// The copy-paste embed snippet a campaign drops into their own site. It is
// self-contained on purpose: a single <iframe> (served from OUR origin, so Stripe.js
// loads on a domain we control) plus a tiny inline resize listener. Nothing extra to
// host, and `allow="payment"` is always present so Apple Pay / Google Pay can surface
// inside the frame.
//
// The iframe reports its content height via postMessage (see useDonationEmbedResize);
// the inline listener trusts a height message only when it came from THIS iframe's
// contentWindow, then resizes it — so the 3-stage form never clips or scrolls.

function toDomSafeToken(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Internal postMessage channel between the embed page (poster) and the snippet's
 * resize listener, namespaced by the current subsidiary brand so nothing off-brand
 * leaks into the copied snippet. Both ends run the same subsidiary build and derive
 * this identically, so they always agree — see embedResize.ts for the poster.
 */
export function donationEmbedHeightMessage(brand = 'signalvote'): string {
  const safeBrand = toDomSafeToken(brand) || 'signalvote';
  return `${safeBrand}:donation:height`;
}

/**
 * A DOM-safe, collision-resistant iframe id: `<brand>-donation-<slug>`. The
 * brand prefix reflects the current subsidiary (e.g. "votecivic", "signalvote")
 * so the copied snippet isn't stamped with an unrelated brand name. Unlike the
 * height-message channel above, the id is self-contained within each freshly
 * copied snippet, so changing it here only affects new copies.
 */
export function donationEmbedIframeId(slug: string, brand = 'signalvote'): string {
  const safeBrand = toDomSafeToken(brand) || 'signalvote';
  const safeSlug = toDomSafeToken(slug) || 'campaign';
  return `${safeBrand}-donation-${safeSlug}`;
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export interface DonationEmbedSnippetOptions {
  /** The origin serving the donation app, e.g. https://votecivic.com. */
  origin: string;
  /** The campaign's public donation slug. */
  slug: string;
  /** Optional committee name for the iframe title (accessibility). */
  committeeName?: string;
  /** Brand prefix for the iframe id, e.g. "votecivic" or "signalvote" — reflects the current subsidiary. */
  brand?: string;
}

export function buildDonationEmbedSnippet({ origin, slug, committeeName, brand }: DonationEmbedSnippetOptions): string {
  const cleanOrigin = origin.replace(/\/+$/, '');
  const id = donationEmbedIframeId(slug, brand);
  const heightMessage = donationEmbedHeightMessage(brand);
  const src = `${cleanOrigin}/embed/${slug}`;
  const title = escapeAttribute(`Donate to ${committeeName ?? 'the campaign'}`);

  return `<iframe
  id="${id}"
  src="${src}"
  title="${title}"
  allow="payment"
  style="width:100%;border:0;min-height:640px;overflow:hidden"
  scrolling="no"
></iframe>
<script>
(function () {
  var frame = document.getElementById("${id}");
  window.addEventListener("message", function (e) {
    if (!e.data || e.data.type !== "${heightMessage}") return;
    if (frame && frame.contentWindow === e.source && typeof e.data.height === "number") {
      frame.style.height = e.data.height + "px";
    }
  });
})();
</script>`;
}
